import { pricingPlansModalMessages } from './pricing-plans-modal.messages';

import { type Period } from '@heidi/commerce/contracts';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCallback, type MouseEvent } from 'react';
import { useIntl } from 'react-intl';
import { getCommerceBillingUiPorts } from '../../billing-ui-ports';
import {
  trackPricingPlansModalCta as trackCta,
  type PricingPlansModalCtaTrackArgs,
} from './track-pricing-plans-modal-cta';
import type { UsePricingPlansModalStateReturn } from './usePricingPlansModalState';

export function usePricingPlansModalCtas(state: UsePricingPlansModalStateReturn) {
  const {
    currentPlan,
    currentSeatType,
    legacyTier,
    handlePlanCheckout,
    handleReactivateSubscription,
    handleClose,
    isTrialingClinician,
    isClinicianStandardPlan,
    isPracticeStandardPlan,
    isPracticeMaxSeat,
    isInReverseTrial,
    isFakeClinicianTrial,
    isCancelPendingSubscription,
  } = state;

  const { formatMessage } = useIntl();

  const clinicianProPlanCtaLabel = formatMessage(
    isClinicianStandardPlan
      ? isTrialingClinician
        ? pricingPlansModalMessages.upgradeNow
        : pricingPlansModalMessages.keepClinician
      : pricingPlansModalMessages.getClinician
  );

  // The Practice column is one card with two seats; each seat is "current"
  // only for the viewer who pays for that exact seat.
  const clinicianProPlusPlanCtaLabel = formatMessage(
    isPracticeStandardPlan
      ? isTrialingClinician
        ? pricingPlansModalMessages.upgradeNow
        : pricingPlansModalMessages.keepPractice
      : pricingPlansModalMessages.getPractice
  );

  const practiceMaxPlanCtaLabel = formatMessage(
    isPracticeMaxSeat
      ? isTrialingClinician
        ? pricingPlansModalMessages.upgradeNow
        : pricingPlansModalMessages.keepPracticeMax
      : pricingPlansModalMessages.getPracticeMax
  );

  const talkToSalesLabel = formatMessage(pricingPlansModalMessages.talkToSalesCta);

  const trackPricingPlansModalCta = useCallback(
    (
      trackArgs: Omit<
        PricingPlansModalCtaTrackArgs,
        'currentPlan' | 'currentSeatType' | 'isInReverseTrial' | 'isFakeClinicianTrial'
      >
    ) =>
      trackCta({
        ...trackArgs,
        currentPlan: currentPlan ?? legacyTier,
        currentSeatType,
        isInReverseTrial,
        isFakeClinicianTrial,
      }),
    [currentPlan, currentSeatType, legacyTier, isInReverseTrial, isFakeClinicianTrial]
  );

  const onClinicianProPlanCtaClick = useCallback(
    (period: Period) => {
      if (getCommerceBillingUiPorts().isAppleCompliantBuild()) return;
      const isOnCurrentPlan = isClinicianStandardPlan && !isTrialingClinician;
      const isKeepCurrentPlanClose = isOnCurrentPlan && !isCancelPendingSubscription;
      trackPricingPlansModalCta(
        isOnCurrentPlan
          ? {
              action: ButtonAction.PricingPlansModalKeepCurrentPlan,
              label: clinicianProPlanCtaLabel,
            }
          : {
              action: ButtonAction.PricingPlansModalGetPlan,
              label: clinicianProPlanCtaLabel,
              targetTier: SubscriptionTier.CLINICIAN_PRO,
              targetSeatType: BillingSeatType.STANDARD,
              period,
            }
      );
      if (isCancelPendingSubscription && isOnCurrentPlan) {
        handleReactivateSubscription();
      } else if (isKeepCurrentPlanClose) {
        handleClose();
      } else {
        void handlePlanCheckout(period, SubscriptionTier.CLINICIAN_PRO);
      }
    },
    [
      clinicianProPlanCtaLabel,
      handleClose,
      handlePlanCheckout,
      handleReactivateSubscription,
      isCancelPendingSubscription,
      isClinicianStandardPlan,
      isTrialingClinician,
      trackPricingPlansModalCta,
    ]
  );

  const handlePracticeCtaClick = useCallback(
    (
      period: Period,
      seatType: BillingSeatType,
      isCurrentSeat: boolean,
      label: string
    ) => {
      if (getCommerceBillingUiPorts().isAppleCompliantBuild()) return;
      const isOnCurrentPlan = isCurrentSeat && !isTrialingClinician;
      const isKeepCurrentPlanClose = isOnCurrentPlan && !isCancelPendingSubscription;
      trackPricingPlansModalCta(
        isOnCurrentPlan
          ? {
              action: ButtonAction.PricingPlansModalKeepCurrentPlan,
              label,
            }
          : {
              action: ButtonAction.PricingPlansModalGetPlan,
              label,
              targetTier: SubscriptionTier.CLINICIAN_PRO_PLUS,
              targetSeatType: seatType,
              period,
            }
      );
      if (isCancelPendingSubscription && isOnCurrentPlan) {
        handleReactivateSubscription();
      } else if (isKeepCurrentPlanClose) {
        handleClose();
      } else {
        void handlePlanCheckout(period, SubscriptionTier.CLINICIAN_PRO_PLUS, seatType);
      }
    },
    [
      handleClose,
      handlePlanCheckout,
      handleReactivateSubscription,
      isCancelPendingSubscription,
      isTrialingClinician,
      trackPricingPlansModalCta,
    ]
  );

  const onClinicianProPlusPlanCtaClick = useCallback(
    (period: Period) =>
      handlePracticeCtaClick(
        period,
        BillingSeatType.STANDARD,
        isPracticeStandardPlan,
        clinicianProPlusPlanCtaLabel
      ),
    [clinicianProPlusPlanCtaLabel, handlePracticeCtaClick, isPracticeStandardPlan]
  );

  const onPracticeMaxPlanCtaClick = useCallback(
    (period: Period) =>
      handlePracticeCtaClick(
        period,
        BillingSeatType.MAX,
        isPracticeMaxSeat,
        practiceMaxPlanCtaLabel
      ),
    [handlePracticeCtaClick, isPracticeMaxSeat, practiceMaxPlanCtaLabel]
  );

  const onTalkToSalesClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      trackPricingPlansModalCta({
        action: ButtonAction.PricingPlansModalGetInTouch,
        label: talkToSalesLabel,
        targetTier: SubscriptionTier.ENTERPRISE,
      });
      // The desktop webview ignores target="_blank"; hand the URL to the shell.
      if (getCommerceBillingUiPorts().isTauri()) {
        event.preventDefault();
        void getCommerceBillingUiPorts().openUrlInTauri(
          getCommerceBillingUiPorts().billingUrls.GET_IN_TOUCH_URL
        );
      }
    },
    [talkToSalesLabel, trackPricingPlansModalCta]
  );

  return {
    clinicianProPlanCtaLabel,
    clinicianProPlusPlanCtaLabel,
    practiceMaxPlanCtaLabel,
    onClinicianProPlanCtaClick,
    onClinicianProPlusPlanCtaClick,
    onPracticeMaxPlanCtaClick,
    onTalkToSalesClick,
    isAppleCompliant: getCommerceBillingUiPorts().isAppleCompliantBuild(),
  };
}
